import {
  BotMessage,
  CustomerMessage,
  InProgressMessage,
  Message,
  ParsedBodyPiece,
} from "@/types/chat"
import {
  WebSearchResultEvent,
  WebSearchResultTrail,
} from "@/types/web-search-result"

export function isCustomerMessage(
  message: Message
): message is CustomerMessage {
  return message.type === "customer"
}

export function isBotMessage(
  message: Message
): message is BotMessage | InProgressMessage {
  return message.type === "bot"
}

export function isPersistentBotMessage(
  message: Message
): message is BotMessage {
  return message.type === "bot" && message.id !== "in-progress"
}

export function isInProgressBotMessage(
  message: Message
): message is InProgressMessage {
  return isBotMessage(message) && message.id === "in-progress"
}

/** Message-scoped place APIs only accept persisted message ids. */
export function messageIdForPlaceApi(
  messageId: Message["id"] | undefined
): number | undefined {
  return messageId === "in-progress" ? undefined : messageId
}

export function hasPlaces(message: Message): boolean {
  return message.places?.length > 0
}

export function hasInspirations(message: Message): boolean {
  return message.inspirations?.length > 0
}

export function resolveStructuredResult(
  message: Message,
  structuredResult: string
): WebSearchResultEvent | WebSearchResultTrail | undefined {
  if (!message.structured_results || !structuredResult) return undefined

  const [type, indexStr] = structuredResult.split(":")
  const index = parseInt(indexStr, 10)

  if (isNaN(index)) return undefined

  if (type === "events" && message.structured_results.events) {
    return message.structured_results.events[index]
  }

  if (type === "trails" && message.structured_results.trails) {
    return message.structured_results.trails[index]
  }

  return undefined
}

export function buildPiecesForMessage(message: Message): ParsedBodyPiece[] {
  let location = 0
  const parsedBody: ParsedBodyPiece[] = []

  // Sort markers by start position to ensure correct display order
  const sortedMarkers = [...message.markers].sort((a, b) => a.start - b.start)

  // Find all signal markers to skip nested markers within them
  const signalRanges = sortedMarkers
    .filter((m) => m.type === "card")
    .map((m) => ({ start: m.start, end: m.end }))

  // Process regular markers (places, inspirations, user mentions)
  sortedMarkers.forEach((marker) => {
    // Skip markers that are nested inside signal markers (they're handled by the signal component)
    if (marker.type !== "card") {
      const isNestedInSignal = signalRanges.some(
        (range) => marker.start >= range.start && marker.end <= range.end
      )
      if (isNestedInSignal) {
        return // Skip this marker
      }
    }
    if (location < marker.start) {
      parsedBody.push({
        id: parsedBody.length + 1,
        type: "text",
        text: message.body.substring(location, marker.start),
      })
    }

    const text = message.body.substring(marker.start, marker.end)
    const isStreaming = false
    const place =
      marker.type === "place" &&
      marker.ref &&
      message.places?.find((e) => e.entity_ref === marker.ref)
    const inspiration =
      marker.type === "inspiration" &&
      marker.ref &&
      message.inspirations?.find((e) => e.entity_ref === marker.ref)
    const airline =
      marker.type === "airline" &&
      marker.ref &&
      message.airlines?.find((e) => e.ref === marker.ref)
    const collection =
      marker.type === "collection" &&
      marker.ref &&
      message.collections?.find((e) => e.ref === marker.ref)
    if (place) {
      parsedBody.push({
        id: parsedBody.length + 1,
        type: "place",
        place,
        text,
        ...(marker.at && { at: marker.at }),
        ...(isStreaming ? { isStreaming } : {}),
      })
    } else if (inspiration) {
      parsedBody.push({
        id: parsedBody.length + 1,
        type: "inspiration",
        inspiration,
        text,
      })
    } else if (airline) {
      parsedBody.push({
        id: parsedBody.length + 1,
        type: "airline",
        airline,
        text,
      })
    } else if (collection) {
      parsedBody.push({
        id: parsedBody.length + 1,
        type: "collection",
        collection,
        text,
      })
    } else if (marker.type === "flight") {
      parsedBody.push({
        id: parsedBody.length + 1,
        type: "flight",
        flight_id: marker.flight_id,
        flight_details: marker.flight_details,
        text,
      })
    } else if (marker.type === "card") {
      const cardPlace =
        marker.ref && message.places?.find((e) => e.entity_ref === marker.ref)
      const cardCollection =
        marker.collection_ref &&
        message.collections?.find((e) => e.ref === marker.collection_ref)

      // Cards have text in the body for backwards compatibility
      // New clients extract the text using marker positions and render as component
      // Old clients without card support see the text normally
      // Allow undefined entity_type during skeleton phase (isStreaming=true)
      parsedBody.push({
        id: parsedBody.length + 1,
        type: "card",
        text, // Text from body at marker positions (for display/accessibility)
        place: cardPlace,
        ...(cardCollection ? { collection: cardCollection } : {}),
        resolved_data: marker.structured_result
          ? resolveStructuredResult(message, marker.structured_result)
          : undefined,
        entity_type: marker.entity_type,
        marker,
      })
    } else if (marker.type === "user" && marker.ref) {
      parsedBody.push({
        id: parsedBody.length + 1,
        type: "user",
        special_type: marker.special_type,
        text,
        user_ref: marker.ref,
      })
    } else if (marker.type === "event") {
      // Handle EventMarker type specifically
      const eventRef = marker.structured_result
      const resolvedData = eventRef
        ? resolveStructuredResult(message, eventRef)
        : undefined
      parsedBody.push({
        id: parsedBody.length + 1,
        type: "event",
        structured_result: eventRef || "",
        resolved_data: resolvedData,
        text,
      })
    } else {
      // unknown entity
      parsedBody.push({
        id: parsedBody.length + 1,
        type: marker.type,
        text,
      })
    }
    location = marker.end
  })

  if (location < message.body.length) {
    parsedBody.push({
      id: parsedBody.length + 1,
      type: "text",
      text: message.body.substring(location),
    })
  }

  // Process events from message.properties
  // Note: events don't have markers, they come directly from properties
  // if (message.search_result_events?.length) {
  //   message.search_result_events.forEach((eventItem: SearchResultEvent) => {
  //     parsedBody.push({
  //       id: parsedBody.length + 1,
  //       type: "event" as const,
  //       event: eventItem,
  //       text: eventItem.name,
  //     })
  //   })
  // }

  return parsedBody
}

export function joinMessagePiecesForMarkdown(pieces: ParsedBodyPiece[]) {
  const content = pieces
    .map((piece) => {
      if (piece.type === "text") {
        return piece.text
      } else {
        return `[[ENTITY-${piece.id}]]`
      }
    })
    .join("")
    // add no-break space after trailing '-' (empty bullet point) so it doesnt make the previous line a header
    // (bot always uses # for headers, not - on next line)
    .replace(/(?<=\s)-\s*$/, "- \u00A0")
    // bot sometimes ends its message with a separator - remove it
    .replace(/\n\s*---\s*$/, "")
    // bot sometimes wraps a markdown link in backticks, which would render it as code - unwrap it
    .replace(/`(\[[^[\]`\n]+\]\(https?:\/\/[^\s()[\]`]+\))`/g, "$1")
    // ensure that separators have a blank line between them and the previous paragraph
    // otherwise it causes the previous paragraph to become a header
    .replace(/\s*\n\s*---\s*(\n\s*|$)/g, "\n\n---\n\n")

  const lines = content.split("\n")
  const processedLines: string[] = []

  for (let i = 0; i < lines.length; i++) {
    const currentLine = lines[i].trim()
    const nextLine = i + 1 < lines.length ? lines[i + 1].trim() : ""

    processedLines.push(lines[i])

    // don't add extra newlines if current or next line looks like table syntax
    const isCurrentLineTable =
      currentLine.startsWith("|") ||
      /^\|.*\|$/.test(currentLine) ||
      /^[|\-\s:]+$/.test(currentLine)
    const isNextLineTable =
      nextLine.startsWith("|") ||
      /^\|.*\|$/.test(nextLine) ||
      /^[|\-\s:]+$/.test(nextLine)

    // add extra newline for paragraph breaks, but not for table rows
    if (
      i < lines.length - 1 &&
      !isCurrentLineTable &&
      !isNextLineTable &&
      currentLine !== "" &&
      nextLine !== ""
    ) {
      processedLines.push("")
    }
  }

  return processedLines.join("\n")
}
